// src/services/fiscalService.js
import { pb } from "./pocketbase";

const N8N_EMISSAO_WEBHOOK_URL = "https://n8n.semprejoias.com.br/webhook/gestao-nfe-emissao";

export const fiscalService = {
  /**
   * Busca todas as configurações fiscais de CNPJs cadastradas no PocketBase.
   * @returns {Promise<Array>} Lista de registros
   */
  async fetchConfigs() {
    try {
      return await pb.collection("fiscal_configs").getFullList({
        sort: "cnpj",
      });
    } catch (error) {
      console.error("Erro ao buscar configurações fiscais:", error);
      throw error;
    }
  },

  /**
   * Salva ou atualiza os dados fiscais de um CNPJ.
   * @param {Object} data - Objeto contendo id, cnpj, razao_social, etc.
   * @returns {Promise<Object>} Registro criado/atualizado
   */
  async saveConfig(data) {
    try {
      const formData = new FormData();
      formData.append("cnpj", data.cnpj);
      formData.append("razao_social", data.razao_social);
      formData.append("inscricao_estadual", data.inscricao_estadual);
      formData.append("inscricao_municipal", data.inscricao_municipal || "");
      formData.append("token_producao", data.token_producao || "");
      formData.append("token_homologacao", data.token_homologacao || "");
      formData.append("ambiente", data.ambiente || "homologacao");
      formData.append("ncm_padrao", data.ncm_padrao || "71131100");
      formData.append("telefone", data.telefone || "");
      formData.append("endereco", data.endereco || "");
      formData.append("bairro", data.bairro || "");
      formData.append("cidade", data.cidade || "");
      formData.append("cep", data.cep || "");
      formData.append("email", data.email || "");

      if (data.logo instanceof File) {
        formData.append("logo", data.logo);
      }
      if (data.assinatura instanceof File) {
        formData.append("assinatura", data.assinatura);
      }

      if (data.id) {
        return await pb.collection("fiscal_configs").update(data.id, formData);
      } else {
        return await pb.collection("fiscal_configs").create(formData);
      }
    } catch (error) {
      console.error("Erro ao salvar configuração fiscal:", error);
      throw error;
    }
  },

  /**
   * Exclui uma configuração fiscal do banco.
   * @param {string} id - ID do registro no PocketBase
   * @returns {Promise<boolean>}
   */
  async deleteConfig(id) {
    try {
      await pb.collection("fiscal_configs").delete(id);
      return true;
    } catch (error) {
      console.error("Erro ao deletar configuração fiscal:", error);
      throw error;
    }
  },

  /**
   * Dispara a requisição de emissão de NF-e para o orquestrador n8n.
   * @param {Object} payload - Dados compilados do emissor, destinatário e itens.
   * @returns {Promise<boolean>} status do disparo
   */
  async requestInvoice(payload) {
    try {
      console.log("📤 Disparando emissão de nota fiscal para o n8n:", payload);
      const response = await fetch(N8N_EMISSAO_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errMessage = `Erro na resposta do webhook do n8n: ${response.status} ${response.statusText}`;
        try {
          const errData = await response.json();
          if (errData) {
            if (errData.mensagem) {
              errMessage = errData.mensagem;
            } else if (errData.error) {
              errMessage = typeof errData.error === 'string' ? errData.error : (errData.error.message || JSON.stringify(errData.error));
            } else if (errData.errorMessage) {
              errMessage = errData.errorMessage;
            } else if (errData.msg) {
              errMessage = errData.msg;
            } else if (errData.msgs && Array.isArray(errData.msgs)) {
              errMessage = errData.msgs.join(" | ");
            } else {
              errMessage = JSON.stringify(errData);
            }
          }
        } catch (_) {
          try {
            const errText = await response.text();
            if (errText) errMessage = errText;
          } catch (__) {}
        }
        throw new Error(errMessage);
      }

      return true;
    } catch (error) {
      console.error("Erro ao solicitar emissão da NF-e para o n8n:", error);
      throw error;
    }
  },

  /**
   * Recupera notas fiscais emitidas de forma paginada e filtrada.
   * @param {number} page - Página atual
   * @param {number} perPage - Quantidade de itens por página
   * @param {string} cnpjFilter - Filtro opcional por CNPJ
   * @param {string} searchTerm - Termo de busca geral
   * @param {string} startDate - Data inicial (YYYY-MM-DD)
   * @param {string} endDate - Data final (YYYY-MM-DD)
   */
  async fetchInvoices(page = 1, perPage = 50, cnpjFilter = "", searchTerm = "", startDate = "", endDate = "", statusFilter = "") {
    try {
      let filterString = 'nfe_status != null && nfe_status != ""';
      
      if (cnpjFilter) {
        const cleanCnpj = cnpjFilter.replace(/\D/g, "");
        filterString += ` && cnpj_emissor = "${cleanCnpj}"`;
      }
      
      if (searchTerm) {
        const escaped = searchTerm.replace(/"/g, '\\"');
        filterString += ` && (order_number ~ "${escaped}" || customer_name ~ "${escaped}" || nfe_numero ~ "${escaped}")`;
      }

      if (startDate) {
        filterString += ` && created >= "${startDate} 00:00:00"`;
      }

      if (endDate) {
        filterString += ` && created <= "${endDate} 23:59:59"`;
      }

      if (statusFilter && statusFilter !== "todas") {
        if (statusFilter === "erro") {
          filterString += ' && (nfe_status = "erro" || nfe_status = "erro_autorizacao")';
        } else {
          filterString += ` && nfe_status = "${statusFilter}"`;
        }
      }

      return await pb.collection("fiscal_invoices").getList(page, perPage, {
        filter: filterString,
        sort: "-created",
        expand: "order",
      });
    } catch (error) {
      console.error("Erro ao buscar notas fiscais:", error);
      throw error;
    }
  },

  /**
   * Recupera TODAS as notas fiscais emitidas no período (sem paginação, para relatórios e exportação).
   * @param {string} cnpjFilter - Filtro opcional por CNPJ
   * @param {string} searchTerm - Termo de busca geral
   * @param {string} startDate - Data inicial (YYYY-MM-DD)
   * @param {string} endDate - Data final (YYYY-MM-DD)
   * @param {string} statusFilter - Filtro por status da nota
   */
  async fetchAllInvoices(cnpjFilter = "", searchTerm = "", startDate = "", endDate = "", statusFilter = "") {
    try {
      let filterString = 'nfe_status != null && nfe_status != ""';
      
      if (cnpjFilter) {
        const cleanCnpj = cnpjFilter.replace(/\D/g, "");
        filterString += ` && cnpj_emissor = "${cleanCnpj}"`;
      }
      
      if (searchTerm) {
        const escaped = searchTerm.replace(/"/g, '\\"');
        filterString += ` && (order_number ~ "${escaped}" || customer_name ~ "${escaped}" || nfe_numero ~ "${escaped}")`;
      }

      if (startDate) {
        filterString += ` && created >= "${startDate} 00:00:00"`;
      }

      if (endDate) {
        filterString += ` && created <= "${endDate} 23:59:59"`;
      }

      if (statusFilter && statusFilter !== "todas") {
        if (statusFilter === "erro") {
          filterString += ' && (nfe_status = "erro" || nfe_status = "erro_autorizacao")';
        } else {
          filterString += ` && nfe_status = "${statusFilter}"`;
        }
      }

      return await pb.collection("fiscal_invoices").getFullList({
        filter: filterString,
        sort: "-created",
        expand: "order",
      });
    } catch (error) {
      console.error("Erro ao buscar todas as notas fiscais:", error);
      throw error;
    }
  },

  /**
   * Dispara o cancelamento de uma nota fiscal pelo webhook do n8n.
   * @param {Object} payload - Dados do cancelamento
   */
  async cancelInvoice(payload) {
    try {
      console.log("📤 Disparando solicitação de cancelamento para o n8n:", payload);
      const N8N_CANCELAMENTO_WEBHOOK_URL = "https://n8n.semprejoias.com.br/webhook/gestao-nfe-cancelamento";
      const response = await fetch(N8N_CANCELAMENTO_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Erro na resposta do webhook: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Erro ao solicitar cancelamento no n8n:", error);
      throw error;
    }
  },

  /**
   * Solicita a sincronização manual do status da nota com a Focus NFe.
   * @param {Object} payload - Dados de identificação e ambiente da nota
   */
  async syncInvoice(payload) {
    try {
      console.log("📤 Solicitando sincronização manual para o n8n:", payload);
      const N8N_CONSULTA_WEBHOOK_URL = "https://n8n.semprejoias.com.br/webhook/gestao-nfe-consulta";
      const response = await fetch(N8N_CONSULTA_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errMessage = `Erro na resposta do webhook: ${response.status}`;
        try {
          const errData = await response.json();
          if (errData) {
            if (errData.mensagem) {
              errMessage = errData.mensagem;
            } else if (errData.error) {
              errMessage = typeof errData.error === 'string' ? errData.error : (errData.error.message || JSON.stringify(errData.error));
            } else if (errData.errorMessage) {
              errMessage = errData.errorMessage;
            } else if (errData.msg) {
              errMessage = errData.msg;
            } else if (errData.msgs && Array.isArray(errData.msgs)) {
              errMessage = errData.msgs.join(" | ");
            } else {
              errMessage = JSON.stringify(errData);
            }
          }
        } catch (_) {
          try {
            const errText = await response.text();
            if (errText) errMessage = errText;
          } catch (__) {}
        }
        throw new Error(errMessage);
      }

      return await response.json();
    } catch (error) {
      console.error("Erro ao sincronizar nota no n8n:", error);
      throw error;
    }
  },

  /**
   * Dispara o envio do e-mail da nota fiscal com anexos.
   * @param {Object} payload - Dados contendo o ref da nota e o pb_token
   */
  async sendInvoiceEmail(payload) {
    try {
      console.log("📤 Disparando webhook para envio de e-mail:", payload);
      const N8N_EMAIL_WEBHOOK_URL = "https://n8n.semprejoias.com.br/webhook/gestao-nfe-enviar-email";
      const response = await fetch(N8N_EMAIL_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Erro na resposta do webhook: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Erro ao enviar e-mail da nota no n8n:", error);
      throw error;
    }
  },

  /**
   * Dispara a inutilização de numeração pelo webhook do n8n.
   * @param {Object} payload - Dados da inutilização (cnpj, ano, serie, numero_inicial, numero_final, justificativa)
   */
  async voidNumberRange(payload) {
    try {
      console.log("📤 Enviando solicitação de inutilização para o n8n:", payload);
      const N8N_INUTILIZACAO_WEBHOOK_URL = "https://n8n.semprejoias.com.br/webhook/gestao-nfe-inutilizacao";
      const response = await fetch(N8N_INUTILIZACAO_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Erro na resposta do webhook: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Erro ao inutilizar numeração no n8n:", error);
      throw error;
    }
  },
};
